import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Input } from "@neworbit/simpleui-input";

import { FileUpload } from "@teamprevent/common/fileUploads";
import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";
import { CurrentUser, isAdminUser } from "@teamprevent/common/auth/roles";

import * as moment from "moment";

import {
    Form,
    Button, Grid,
    Segment,
} from "semantic-ui-react";

import { ThunkDispatch } from "redux-thunk";

import { createCaseMessage } from "../actions";

import { Message, message, MessagingDocument } from "../models";

import "../styles/case-messaging.scss";
import { CaseConversationDocumentText } from "./CaseConversationDocumentText";

export interface CaseMessagingProps {
    caseId: string;
    customerId: string;
    onSubmit: () => void;
}

const CaseMessaging: React.FC<CaseMessagingProps> = ({ caseId, onSubmit, customerId }) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    const [newMessage, setNewMessage] = React.useState<Message>(message);
    const [submitted, setSubmitted] = React.useState<boolean>(false);

    const caseConversationImprovements = getFeatureToggles()[FeatureToggleNames.CaseConversationImprovements];
    const currentUser = useSelector((state: { currentUser: CurrentUser }) => state.currentUser);
    const isAdmin = isAdminUser(currentUser);

    const onInputChanged = (fieldName: string) => {
        return (value: string) => {
            setNewMessage({ ...newMessage, [fieldName]: value });
        };
    };

    const onInputChangedInTextSnippet = (fieldName: keyof Message) => {
        const value = newMessage[fieldName] as string;
        return (snippetText: string) => {
            setNewMessage({ ...newMessage, [fieldName]: value + " " + snippetText });
        };
    };

    const clearMessageForm = React.useCallback(() => {
        setNewMessage(message);
        setSubmitted(false);
    }, []);

    const onNewMessageSubmit = React.useCallback(async () => {
        setSubmitted(true);

        if (!newMessage.message || !newMessage.subject) {
            setSubmitted(false);
            return;
        }

        await dispatch(createCaseMessage(customerId, caseId, newMessage));
        onSubmit();
        clearMessageForm();
    }, [newMessage, dispatch, customerId, caseId, onSubmit, clearMessageForm]);

    const documentUploaded = (ohCaseDocumentId: string, fileName: string) => {
        setNewMessage({
            ...newMessage,
            documents: [...newMessage.documents,
                { blobId: ohCaseDocumentId, fileName, dateCreated: moment.utc() }]
        });
    };

    const removeEvidence = (docId: string): void => {
        setNewMessage({
            ...newMessage,
            documents: [
                ...newMessage.documents.filter(m => m.blobId !== docId),
            ]
        });
    };

    return (
        <div>
            <Form>
                <Form.Field required>
                    <Input.Text
                        required
                        value={newMessage.subject}
                        showErrors={submitted}
                        label="Subject"
                        onChange={onInputChanged("subject")}
                    />
                </Form.Field>
                <Form.Field required>
                    {isAdmin && caseConversationImprovements && <Segment textAlign="left" basic compact>
                        <TextSnippetSelector onSelect={onInputChangedInTextSnippet("message")} />
                    </Segment>}
                    <Input.Textarea
                        placeholder={"Message..."}
                        required
                        value={newMessage.message}
                        showErrors={submitted}
                        label="Message"
                        onChange={onInputChanged("message")}
                        rows={5}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Documents</label>
                    <FileUpload
                        apiUrl={`/api/Messaging/${caseId}/evidence`}
                        // eslint-disable-next-line react/jsx-no-bind
                        onFileChange={documentUploaded}
                        acceptExtensions={acceptableExtensions}
                        showFileName={false}
                    />
                    <CaseConversationDocumentText className="documents-upload-note" />
                </Form.Field>
                <Form.Field>
                    {newMessage.documents.map((doc: MessagingDocument, index) => (
                        <Grid key={index}>
                            <Grid.Row columns={2}>
                                <Grid.Column>
                                    <p key={index}>{doc.fileName}</p>
                                </Grid.Column>
                                <Grid.Column>
                                    <Button
                                        className="remove-evidence-button"
                                        content="Remove"
                                        type="button"
                                        // eslint-disable-next-line react/jsx-no-bind
                                        onClick={() => removeEvidence(doc.blobId)}
                                        floated="right"
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    ))}
                </Form.Field>
                <Button onClick={clearMessageForm}>Clear</Button>
                <Button disabled={submitted} onClick={onNewMessageSubmit}>Send</Button>
            </Form>
        </div>
    );
};

export {
    CaseMessaging
};
